import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { db, storage } from '../../../firebase/config';
import {
  doc, getDoc, addDoc, updateDoc, collection, serverTimestamp, query, where, getDocs,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Box, Typography, Button, TextField, CircularProgress,
  LinearProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PersonIcon from '@mui/icons-material/Person';

const bg = '#1a1a1a';
const surface = '#242424';
const accent = '#E05500';

/**
 * GuestJoin — ange namn för att delta, utan inloggning.
 * Visar workshopnamnet och ett namnfält. Enkelt och tillgängligt för alla.
 */
function GuestJoin({ sessionTitle, onJoin }) {
  const [name, setName] = useState('');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 380 }}>
        <Typography color={accent} fontSize="0.75rem" fontWeight={700} letterSpacing={1} mb={1} textAlign="center">
          WORKSHOPN
        </Typography>
        {sessionTitle && (
          <Typography variant="h6" color="#fff" fontWeight={700} mb={1} textAlign="center" sx={{ lineHeight: 1.3 }}>
            {sessionTitle}
          </Typography>
        )}
        <Typography color="#999" textAlign="center" mb={4} fontSize="0.88rem">
          Ange ditt namn för att delta
        </Typography>

        <TextField
          fullWidth
          autoFocus
          placeholder="Ditt namn"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && name.trim()) onJoin(name.trim()); }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              color: '#fff', bgcolor: surface, fontSize: '1rem',
              '& fieldset': { borderColor: '#444' },
              '&:hover fieldset': { borderColor: '#666' },
              '&.Mui-focused fieldset': { borderColor: accent },
            },
            '& .MuiInputBase-input::placeholder': { color: '#666', opacity: 1 },
          }}
        />

        <Button
          fullWidth
          variant="contained"
          disabled={!name.trim()}
          onClick={() => onJoin(name.trim())}
          startIcon={<PersonIcon />}
          sx={{
            bgcolor: accent, '&:hover': { bgcolor: '#C94D00' },
            '&.Mui-disabled': { bgcolor: '#333', color: '#555' },
            textTransform: 'none', fontWeight: 600, fontSize: '0.95rem', py: 1.5,
            borderRadius: 2,
          }}
        >
          Gå med i workshopen
        </Button>
      </Box>
    </Box>
  );
}

export default function WorkshopNParticipant() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile, loading: authLoading } = useAuth();

  // Gästnamn — används om currentUser saknas
  const [guestName, setGuestName] = useState('');

  const [session, setSession]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [groupId, setGroupId]       = useState(searchParams.get('group') || '');
  const [step, setStep]             = useState('group'); // 'group' | 'questions' | 'done'
  const [currentQ, setCurrentQ]     = useState(0);
  const [answers, setAnswers]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);
  const [existingResponseId, setExistingResponseId] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploaded, setPhotoUploaded] = useState(false);

  // Identitet: antingen inloggad eller gäst med namn
  const hasIdentity = !!currentUser || !!guestName;
  const displayName = guestName || userProfile?.displayName || currentUser?.displayName || currentUser?.email || '';

  useEffect(() => {
    // Vänta tills auth-tillståndet är klart och vi har en identitet
    if (authLoading) return;
    if (!hasIdentity) return;

    const load = async () => {
      try {
        // QR-kods-URL:er använder sessionCode (t.ex. "KATTEN"), direktlänkar använder doc-ID.
        // Prova sessionCode-sökning först, falla tillbaka på doc-ID-sökning.
        let resolvedSnap;
        const byCode = await getDocs(query(collection(db, 'workshops'), where('sessionCode', '==', sessionId)));
        if (!byCode.empty) {
          resolvedSnap = byCode.docs[0];
        } else {
          const byId = await getDoc(doc(db, 'workshops', sessionId));
          if (byId.exists()) resolvedSnap = byId;
        }
        if (!resolvedSnap) { setError('Workshopen finns inte.'); return; }
        const data = resolvedSnap.data();
        if (data.status?.locked) { setError('Workshopen är låst för nya svar.'); return; }
        setSession({ id: resolvedSnap.id, ...data });

        // Kolla om inloggad användare redan svarat (gäster hoppar över)
        // Använd resolvedSnap.id (faktiskt doc-ID) — sessionId i URL kan vara sessionCode
        if (currentUser) {
          const existing = await getDocs(
            query(collection(db, 'workshops', resolvedSnap.id, 'responses'),
              where('userId', '==', currentUser.uid))
          );
          if (!existing.empty) {
            const existingDoc = existing.docs[0];
            setAlreadyAnswered(true);
            setExistingResponseId(existingDoc.id);
            const prevData = existingDoc.data();
            if (prevData.answers) setAnswers(prevData.answers);
            if (prevData.groupId) setGroupId(prevData.groupId);
          }
        }

        // Förvald grupp från URL
        const preGroup = searchParams.get('group');
        if (preGroup) {
          setGroupId(preGroup);
          setStep('questions');
        }
      } catch (e) {
        setError('Kunde inte ladda workshopen.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionId, currentUser, authLoading, hasIdentity, searchParams]);

  const handleGroupSelect = (gid) => {
    setGroupId(gid);
    setStep('questions');
  };

  const currentQuestion = session?.config?.questions?.[currentQ];
  const totalQuestions  = session?.config?.questions?.length || 0;

  const setAnswer = (val) => setAnswers(a => ({ ...a, [currentQ]: val }));

  const handleNext = () => {
    if (currentQ < totalQuestions - 1) {
      setCurrentQ(q => q + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const wsId = session.id; // alltid Firestore doc-ID, oavsett vad URL:en innehåller
      const group = session.config.groups.find(g => g.id === groupId);
      const payload = {
        ...(currentUser ? { userId: currentUser.uid } : {}),
        displayName,
        groupId,
        groupName: group?.name || '',
        answers,
        submittedAt: serverTimestamp(),
      };
      if (existingResponseId) {
        await updateDoc(doc(db, 'workshops', wsId, 'responses', existingResponseId), payload);
      } else {
        await addDoc(collection(db, 'workshops', wsId, 'responses'), payload);
      }
      setStep('done');
    } catch (e) {
      setError('Kunde inte skicka svar. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  // Auth laddas fortfarande
  if (authLoading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  // Ingen identitet — visa namnformulär (fungerar för alla, oavsett LagbasN-konto)
  if (!hasIdentity) return (
    <GuestJoin sessionTitle={session?.title} onJoin={name => setGuestName(name)} />
  );

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  if (error) return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Typography color="#ef4444" textAlign="center">{error}</Typography>
    </Box>
  );

  if (alreadyAnswered && step !== 'done' && step !== 'questions') return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 56, color: accent, mb: 2 }} />
      <Typography variant="h6" color="#fff" textAlign="center" mb={1}>{session?.title}</Typography>
      <Typography color="#999" textAlign="center" mb={3}>Du har redan skickat in dina svar.</Typography>
      <Button
        variant="outlined"
        onClick={() => setStep('questions')}
        sx={{ borderColor: accent, color: accent, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
      >
        Redigera mina svar
      </Button>
    </Box>
  );

  // ── Välj grupp ────────────────────────────────────────────────────────────────
  if (step === 'group') return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 440 }}>
        <Typography color={accent} fontSize="0.8rem" fontWeight={700} letterSpacing={1} mb={1}>
          {session?.title}
        </Typography>
        <Typography variant="h5" color="#fff" fontWeight={700} mb={session?.mode === 'hybrid' ? 2 : 4}>
          Välj din grupp
        </Typography>
        {session?.mode === 'hybrid' && (
          <Box sx={{ bgcolor: '#ffffff11', border: '1px solid #ffffff22', borderRadius: 2, px: 2, py: 1.5, mb: 3 }}>
            <Typography color="#fff" fontSize="0.85rem" fontWeight={600} mb={0.5}>Hybrid-session</Typography>
            <Typography color="#aaa" fontSize="0.8rem" sx={{ lineHeight: 1.5 }}>
              Du kan delta på plats eller på distans — svara i din mobil/dator. Svaren visas på storbild för alla.
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {session?.config?.groups?.map(g => (
            <Button
              key={g.id}
              fullWidth
              variant="outlined"
              onClick={() => handleGroupSelect(g.id)}
              sx={{
                borderColor: g.color || '#444',
                color: '#fff',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                py: 1.5,
                borderRadius: 2,
                justifyContent: 'flex-start',
                pl: 2,
                '&:hover': { bgcolor: (g.color || accent) + '22', borderColor: g.color || accent },
              }}
            >
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: g.color || '#666', mr: 1.5, flexShrink: 0 }} />
              {g.name}
            </Button>
          ))}
        </Box>
        <Typography color="#666" fontSize="0.75rem" mt={3} textAlign="center">
          {displayName}
        </Typography>
      </Box>
    </Box>
  );

  const handleParticipantPhotoUpload = async (file) => {
    if (!session?.id) return;
    setPhotoUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const photoId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
      const storageRef = ref(storage, `workshops/${session.id}/photos/${photoId}.${ext}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const existing = session.photos || [];
      await updateDoc(doc(db, 'workshops', session.id), {
        photos: [...existing, { id: photoId, url, name: file.name, uploadedAt: new Date().toISOString(), uploadedBy: displayName }],
      });
      setPhotoUploaded(true);
    } catch (e) {
      console.error('Photo upload failed:', e);
    }
    setPhotoUploading(false);
  };

  // ── Klart ─────────────────────────────────────────────────────────────────────
  if (step === 'done') return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#4ade80', mb: 3 }} />
      <Typography variant="h5" color="#fff" fontWeight={700} mb={1} textAlign="center">
        Tack, {displayName}!
      </Typography>
      <Typography color="#999" textAlign="center" sx={{ mb: 4 }}>
        Dina svar har skickats in.
      </Typography>
      <Box sx={{ bgcolor: '#242424', border: '1px solid #333', borderRadius: 2, p: 2.5, width: '100%', maxWidth: 360, textAlign: 'center' }}>
        <CameraAltIcon sx={{ fontSize: 32, color: accent, mb: 1 }} />
        <Typography color="#fff" fontWeight={700} mb={0.5}>Dela ett foto från workshopen</Typography>
        <Typography color="#888" fontSize="0.82rem" sx={{ mb: 2 }}>
          Foton visas i karusell i enkäten och presentationsläget.
        </Typography>
        {photoUploaded ? (
          <Typography color="#4ade80" fontWeight={600} fontSize="0.88rem">✓ Foto uppladdat!</Typography>
        ) : (
          <Button component="label" variant="outlined" disabled={photoUploading}
            startIcon={photoUploading ? <CircularProgress size={16} sx={{ color: accent }} /> : <CameraAltIcon />}
            sx={{ borderColor: accent, color: accent, textTransform: 'none', fontWeight: 600 }}>
            {photoUploading ? 'Laddar upp...' : 'Ta eller välj foto'}
            <input type="file" hidden accept="image/*" capture="environment"
              onChange={e => { if (e.target.files?.[0]) handleParticipantPhotoUpload(e.target.files[0]); e.target.value = ''; }} />
          </Button>
        )}
      </Box>
    </Box>
  );

  // ── Frågor ────────────────────────────────────────────────────────────────────
  const isLast = currentQ === totalQuestions - 1;
  const qType = currentQuestion?.type || 'fritext';
  const canProceed = (qType === 'skala' || qType === 'flerval')
    ? (answers[currentQ] !== undefined && answers[currentQ] !== '')
    : (!currentQuestion?.required || (answers[currentQ] !== undefined && answers[currentQ] !== ''));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 560, mt: { xs: 2, md: 6 } }}>
        {/* Progress */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography color="#666" fontSize="0.75rem">
              {session?.title}
            </Typography>
            <Typography color="#666" fontSize="0.75rem">
              {currentQ + 1} / {totalQuestions}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={((currentQ + 1) / totalQuestions) * 100}
            sx={{
              bgcolor: '#333',
              '& .MuiLinearProgress-bar': { bgcolor: accent },
              borderRadius: 4,
              height: 4,
            }}
          />
        </Box>

        {/* Fråga */}
        <Typography variant="h5" color="#fff" fontWeight={700} mb={1} sx={{ lineHeight: 1.3 }}>
          {currentQuestion?.text}
        </Typography>
        {currentQuestion?.hint && (
          <Typography color="#999" mb={3} fontSize="0.9rem" sx={{ lineHeight: 1.5 }}>
            {currentQuestion.hint}
          </Typography>
        )}

        {/* Svarinput */}
        {qType === 'fritext' && (
          <TextField
            multiline minRows={4}
            fullWidth
            placeholder="Skriv ditt svar här..."
            value={answers[currentQ] || ''}
            onChange={e => setAnswer(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: '#fff', bgcolor: surface,
                '& fieldset': { borderColor: '#444' },
                '&:hover fieldset': { borderColor: '#666' },
                '&.Mui-focused fieldset': { borderColor: accent },
              },
            }}
          />
        )}

        {qType === 'ordmoln' && (
          <TextField
            fullWidth
            placeholder="Skriv ord separerade med komma..."
            value={answers[currentQ] || ''}
            onChange={e => setAnswer(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: '#fff', bgcolor: surface,
                '& fieldset': { borderColor: '#444' },
                '&:hover fieldset': { borderColor: '#666' },
                '&.Mui-focused fieldset': { borderColor: accent },
              },
            }}
          />
        )}

        {qType === 'skala' && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
            {[1, 2, 3, 4, 5].map(n => (
              <Button
                key={n}
                variant={answers[currentQ] === String(n) ? 'contained' : 'outlined'}
                onClick={() => setAnswer(String(n))}
                sx={{
                  minWidth: 56, height: 56, fontSize: '1.2rem', fontWeight: 700,
                  bgcolor: answers[currentQ] === String(n) ? accent : 'transparent',
                  borderColor: answers[currentQ] === String(n) ? accent : '#444',
                  color: '#fff',
                  '&:hover': { bgcolor: accent + '33', borderColor: accent },
                }}
              >
                {n}
              </Button>
            ))}
          </Box>
        )}

        {qType === 'flerval' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {(currentQuestion.options || []).map((opt, oi) => {
              const selected = answers[currentQ] === opt;
              return (
                <Button
                  key={oi}
                  variant={selected ? 'contained' : 'outlined'}
                  onClick={() => setAnswer(opt)}
                  fullWidth
                  sx={{
                    textTransform: 'none', justifyContent: 'flex-start', fontWeight: selected ? 700 : 400,
                    bgcolor: selected ? accent : 'transparent',
                    borderColor: selected ? accent : '#444',
                    color: '#fff', py: 1.5, px: 2,
                    '&:hover': { bgcolor: accent + '33', borderColor: accent },
                  }}
                >
                  {opt}
                </Button>
              );
            })}
          </Box>
        )}

        {/* Nästa */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleNext}
          disabled={!canProceed || submitting}
          sx={{
            bgcolor: accent, '&:hover': { bgcolor: '#C94D00' },
            textTransform: 'none', fontWeight: 700, fontSize: '1rem', py: 1.5,
            borderRadius: 2,
            '&.Mui-disabled': { bgcolor: '#333', color: '#666' },
          }}
        >
          {submitting
            ? <CircularProgress size={22} sx={{ color: '#fff' }} />
            : isLast ? (existingResponseId ? 'Uppdatera svar' : 'Skicka in') : 'Nästa'}
        </Button>

        {!currentQuestion?.required && (
          <Button
            fullWidth
            onClick={() => { setAnswer(''); handleNext(); }}
            sx={{ mt: 1, color: '#666', textTransform: 'none' }}
          >
            Hoppa över
          </Button>
        )}
      </Box>
    </Box>
  );
}
